const TRANSIENT_CALLABLE_CODES = new Set([
  'functions/internal',
  'functions/unavailable',
  'functions/resource-exhausted',
]);

type AdmissionState = {
  tail: Promise<void>;
};

type AdmissionEpoch = {
  generation: number;
  invalidationListeners: Set<() => void>;
};

type AdmissionGlobal = typeof globalThis & {
  __kopCallableReadAdmissionV1?: Map<string, AdmissionState>;
  __kopCallableReadInFlightV1?: Map<string, Promise<unknown>>;
  __kopCallableReadEpochV1?: AdmissionEpoch;
};

const operationalLaneWidth = 3;
export const CALLABLE_READ_ADMISSION_INVALIDATED = 'CALLABLE_READ_ADMISSION_INVALIDATED';

function stableJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableJson).join(',')}]`;
  return `{${Object.entries(value as Record<string, unknown>)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([key, child]) => `${JSON.stringify(key)}:${stableJson(child)}`)
    .join(',')}}`;
}

function laneSlot(key: string, width: number): number {
  let hash = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % width;
}

export function callableReadKey(identityId: string, operation: string, payload: unknown): string {
  const identity = identityId.trim();
  const name = operation.trim();
  if (!identity || !name) throw new Error('CALLABLE_READ_KEY_INVALID');
  return `${identity}:${name}:${stableJson(payload)}`;
}

const admissionState = (lane: string): AdmissionState => {
  const shared = globalThis as AdmissionGlobal;
  const lanes = (shared.__kopCallableReadAdmissionV1 ??= new Map());
  const current = lanes.get(lane);
  if (current) return current;
  const created = { tail: Promise.resolve() };
  lanes.set(lane, created);
  return created;
};

const createAdmissionEpoch = (generation: number): AdmissionEpoch => ({
  generation,
  invalidationListeners: new Set(),
});

const admissionEpoch = (): AdmissionEpoch => {
  const shared = globalThis as AdmissionGlobal;
  return (shared.__kopCallableReadEpochV1 ??= createAdmissionEpoch(0));
};

const invalidatedError = () =>
  Object.assign(new Error('Callable read was invalidated by an authentication transition.'), {
    code: CALLABLE_READ_ADMISSION_INVALIDATED,
  });

const assertCurrentEpoch = (epoch: AdmissionEpoch) => {
  if ((globalThis as AdmissionGlobal).__kopCallableReadEpochV1 !== epoch) throw invalidatedError();
};

const raceWithInvalidation = <T>(epoch: AdmissionEpoch, work: Promise<T>): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    let settled = false;
    const finish = (settle: () => void) => {
      if (settled) return;
      settled = true;
      epoch.invalidationListeners.delete(onInvalidated);
      settle();
    };
    const onInvalidated = () => finish(() => reject(invalidatedError()));
    epoch.invalidationListeners.add(onInvalidated);
    if ((globalThis as AdmissionGlobal).__kopCallableReadEpochV1 !== epoch) {
      onInvalidated();
    }
    work.then(
      (value) => finish(() => resolve(value)),
      (error: unknown) => finish(() => reject(error)),
    );
  });

/**
 * Invalidates queued, retrying and unresolved reads when authentication authority changes.
 * Admission lane tails are deliberately preserved so an unresolved prior network attempt still
 * bounds later starts instead of allowing a new session to fan out around the concurrency gate.
 */
export function invalidateCallableReadAdmission(): void {
  const shared = globalThis as AdmissionGlobal;
  const previous = admissionEpoch();
  shared.__kopCallableReadEpochV1 = createAdmissionEpoch(previous.generation + 1);
  for (const invalidate of previous.invalidationListeners) invalidate();
  previous.invalidationListeners.clear();
}

const callableCode = (error: unknown): string =>
  typeof error === 'object' && error !== null && 'code' in error
    ? String((error as { code?: unknown }).code ?? '')
    : '';

export const isTransientCallableReadFailure = (error: unknown): boolean =>
  TRANSIENT_CALLABLE_CODES.has(callableCode(error));

const delay = (milliseconds: number): Promise<void> =>
  new Promise((resolve) => globalThis.setTimeout(resolve, milliseconds));

async function locallySerialized<T>(lane: string, work: () => Promise<T>): Promise<T> {
  const state = admissionState(lane);
  const previous = state.tail.catch(() => undefined);
  let release: () => void = () => {};
  state.tail = new Promise<void>((resolve) => {
    release = resolve;
  });
  await previous;
  try {
    return await work();
  } finally {
    release();
  }
}

async function crossTabSerialized<T>(lane: string, work: () => Promise<T>): Promise<T> {
  const locks = (
    globalThis.navigator as
      | (Navigator & {
          locks?: { request<T>(name: string, callback: () => Promise<T>): Promise<T> };
        })
      | undefined
  )?.locks;
  if (!locks) return locallySerialized(lane, work);
  return locks.request(`kop-callable-read-startup-v1:${lane}`, () => locallySerialized(lane, work));
}

/**
 * Serializes top-level feature reads while regional Cloud Run capacity is constrained.
 * Each network attempt holds a slot; retry backoff releases it so unrelated reads
 * can proceed. Retried attempts rejoin admission with the same concurrency bound.
 */
async function runAdmittedCallableReadAtEpoch<T>(
  epoch: AdmissionEpoch,
  read: () => Promise<T>,
  delays: readonly number[] = [1_200, 3_500, 8_000, 15_000],
  lane = 'operational',
): Promise<T> {
  let attempt = 0;
  for (;;) {
    try {
      return await raceWithInvalidation(
        epoch,
        crossTabSerialized(lane, async () => {
          assertCurrentEpoch(epoch);
          const result = await read();
          assertCurrentEpoch(epoch);
          return result;
        }),
      );
    } catch (error) {
      assertCurrentEpoch(epoch);
      const baseDelay = delays[attempt];
      if (!isTransientCallableReadFailure(error) || baseDelay === undefined) throw error;
      attempt += 1;
      const jitter = 0.8 + Math.random() * 0.4;
      await raceWithInvalidation(epoch, delay(Math.round(baseDelay * jitter)));
    }
  }
}

export function runAdmittedCallableRead<T>(
  read: () => Promise<T>,
  delays: readonly number[] = [1_200, 3_500, 8_000, 15_000],
  lane = 'operational',
): Promise<T> {
  return runAdmittedCallableReadAtEpoch(admissionEpoch(), read, delays, lane);
}

/**
 * Coalesces an identical in-flight read for the same authenticated identity.
 * Settled values are never cached, so a command followed by a read always reaches
 * the authoritative server. Operational reads use three deterministic admission
 * slots: enough parallelism for one screen's independent panels without allowing
 * an unbounded feature fan-out during a regional cold start.
 */
export function runKeyedAdmittedCallableRead<T>(
  key: string,
  read: () => Promise<T>,
  delays: readonly number[] = [1_200, 3_500, 8_000, 15_000],
  lane = 'operational',
): Promise<T> {
  if (!key.trim()) return Promise.reject(new Error('CALLABLE_READ_KEY_INVALID'));
  const shared = globalThis as AdmissionGlobal;
  const inFlight = (shared.__kopCallableReadInFlightV1 ??= new Map());
  const epoch = admissionEpoch();
  const compoundKey = `${epoch.generation}:${lane}:${key}`;
  const existing = inFlight.get(compoundKey);
  if (existing) return existing as Promise<T>;
  const admittedLane =
    lane === 'operational' ? `${lane}:slot-${laneSlot(key, operationalLaneWidth)}` : lane;
  const pending = runAdmittedCallableReadAtEpoch(epoch, read, delays, admittedLane).finally(() => {
    if (inFlight.get(compoundKey) === pending) inFlight.delete(compoundKey);
  });
  inFlight.set(compoundKey, pending);
  return pending;
}

export function resetCallableReadAdmissionForTests(): void {
  delete (globalThis as AdmissionGlobal).__kopCallableReadAdmissionV1;
  delete (globalThis as AdmissionGlobal).__kopCallableReadInFlightV1;
  delete (globalThis as AdmissionGlobal).__kopCallableReadEpochV1;
}
